// ViewContact.jsx
// Route: /viewcontact/:contactId
// Add to router: <Route path="/viewcontact/:contactId" element={<ViewContact />} />

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center" style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }} onClick={() => setOpen(!open)}>
        <span>{title}</span><span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const initials = (name = "") => name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "??";
const formatDateTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function ViewContact() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [contact, setContact] = useState(null);
  const [allContacts, setAllContacts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("address");
  const [activeSubTab, setActiveSubTab] = useState("systemNotes");
  const [navbarColor, setNavbarColor] = useState("#080235");

  // Bootstrap loader
  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const il = document.createElement("link"); il.rel = "stylesheet"; il.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(il);
    return () => { bl.remove(); il.remove(); };
  }, []);

  // Auth check
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) { toast.error("Unauthorized."); navigate("/"); return; }
  }, [navigate, token]);

  // Navbar color
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!token || !userId) return;
    fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, [token]);

  // Load all contacts for prev/next navigation
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/contacts/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setAllContacts(Array.isArray(data) ? data : data.content || []))
      .catch(() => { });
  }, [token]);

  // Load this contact
  useEffect(() => {
    if (!contactId) return;
    fetch(`${API_BASE_URL}/api/contacts/${contactId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setContact(data); else toast.error("Contact not found."); })
      .catch(() => toast.error("Failed to load contact."));
  }, [contactId, token]);

  // Load audit logs
  useEffect(() => {
    if (!contactId) return;
    fetch(`${API_BASE_URL}/api/audit/contact/${contactId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : []).then(setAuditLogs).catch(() => setAuditLogs([]));
  }, [contactId, token]);

  // Prev / Next
  const contactIds = allContacts.map((c) => Number(c.id));
  const currentIndex = contactIds.indexOf(Number(contactId));
  const goToPrev = () => {
    if (currentIndex > 0) navigate(`/viewcontact/${contactIds[currentIndex - 1]}`);
    else toast.info("This is the first contact.");
  };
  const goToNext = () => {
    if (currentIndex < contactIds.length - 1) navigate(`/viewcontact/${contactIds[currentIndex + 1]}`);
    else toast.info("This is the last contact.");
  };

  // Set primary
  const handleSetPrimary = async () => {
    if (!contact?.customerId) return;
    try {
      await fetch(`${API_BASE_URL}/api/customers/${contact.customerId}/contacts/${contactId}/primary`, { method: "PUT", headers: { Authorization: `Bearer ${token}` } });
      toast.success("Set as primary contact.");
      setContact((prev) => ({ ...prev, primary: true }));
    } catch { toast.error("Failed to set primary."); }
  };

  if (!contact) return <div className="p-4">Loading…</div>;

  const c = contact;

  return (
    <>
      <div className="fixed-top"><AdminNavbar /></div>

      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        {/* Breadcrumb */}
        <nav className="mb-2" style={{ fontSize: 13, color: "#888" }}>
          <Link to="/allcontacts" style={{ color: "#0052CC", textDecoration: "none" }}>Contacts</Link>
          <span className="mx-1">/</span>
          <span className="text-dark fw-semibold">{c.name}</span>
        </nav>

        {/* Header */}
        <div className="border-bottom pb-2 mb-3">
          {/* Top Row */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center">
              <i className="bi bi-person fs-3 me-2 text-primary"></i>
              <h5 className="m-0 me-3">Contact</h5>
            </div>
            <div className="d-flex align-items-center">
              <i className="bi bi-arrow-left-circle me-3" style={{ cursor: "pointer", fontSize: "1.3rem", color: "#0d6efd" }} title="Previous Contact" onClick={goToPrev} />
              <i className="bi bi-arrow-right-circle me-3" style={{ cursor: "pointer", fontSize: "1.3rem", color: "#0d6efd" }} title="Next Contact" onClick={goToNext} />
              <span style={{ cursor: "pointer", color: "blue", fontSize: 14 }} onClick={() => navigate("/allcontacts")}>List</span>
            </div>
          </div>

          {/* Avatar + Name + Badges */}
          <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
            <div style={{ width: 44, height: 44, borderRadius: 10, background: c.primary ? "#dbeafe" : "#eaf0f8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, color: c.primary ? "#1d4ed8" : "#3a6090", marginRight: 10, flexShrink: 0 }}>
              {initials(c.name)}
            </div>
            <h4 className="m-0 me-2">{c.name}</h4>
            {c.primary && <span className="badge" style={{ background: "#1d4ed8", color: "#fff", fontSize: 12 }}>★ Primary</span>}
          </div>

          {/* Action Buttons */}
          <div className="d-flex align-items-center gap-2 mb-2">
            <button className="btn btn-primary btn-sm" onClick={() => navigate(`/editcontact/${contactId}`)}>Edit</button>
            <button className="btn btn-light btn-sm" onClick={() => navigate("/allcontacts")}>Back</button>
          </div>
        </div>

        {/* Basic Info — address field removed from here */}
        <Section title="Contact Information">
          <div className="col-md-3"><strong>Full Name</strong><br />{c.name || "—"}</div>
          <div className="col-md-3"><strong>Email</strong><br />{c.email ? <a href={`mailto:${c.email}`} style={{ color: "#0052CC" }}>{c.email}</a> : "—"}</div>
          <div className="col-md-3"><strong>Phone</strong><br />{c.phone || "—"}</div>
          <div className="col-md-3"><strong>Designation</strong><br />{c.designation || "—"}</div>
          <div className="col-md-3"><strong>Department</strong><br />{c.department || "—"}</div>
          <div className="col-md-3">
            <strong>Primary Contact</strong><br />
            {c.primary ? <span className="badge" style={{ background: "#1d4ed8", color: "#fff" }}>★ Yes</span> : <span className="text-muted">No</span>}
          </div>
        </Section>

        {/* Customer quick card */}
        {(c.customerName || c.customerId) && (
          <div className="mb-3 p-3 border rounded bg-white d-flex align-items-center gap-3">
            <i className="bi bi-building text-primary" style={{ fontSize: 20 }} />
            <div>
              <div className="fw-semibold" style={{ fontSize: 14 }}>{c.customerName || "—"}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>Customer</div>
            </div>
            {c.customerId && (
              <Link to={`/viewcustomer/${c.customerId}`} className="btn btn-sm btn-outline-primary ms-auto">View Customer</Link>
            )}
          </div>
        )}

        {/* Tabs */}
        <ul className="nav nav-tabs mb-2 fw-semibold" style={{ backgroundColor: navbarColor }}>
          {[
            ["address", "Address"],
            ["details", "Notes"],
            ["system", "System Information"],
          ].map(([tabId, label]) => (
            <li className="nav-item" key={tabId}>
              <button
                type="button"
                className={`nav-link ${activeTab === tabId ? "active" : ""}`}
                onClick={() => setActiveTab(tabId)}
                style={{
                  color: activeTab === tabId ? navbarColor : "#fff",
                  backgroundColor: activeTab === tabId ? "#fff" : "transparent",
                  fontWeight: activeTab === tabId ? "600" : "normal",
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>

        <div className="border p-3">

          {/* Notes tab */}
          {activeTab === "details" && (
            c.notes
              ? <div><strong>Notes</strong><div className="mt-2 p-3 border rounded bg-light" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{c.notes}</div></div>
              : <p className="text-muted mb-0">No notes added.</p>
          )}

          {/* Address tab */}
          {/* Address tab */}
          {activeTab === "address" && (
            <div>
              {(c.addresses || []).length === 0 ? (
                <p className="text-muted mb-0">No addresses added.</p>
              ) : (
                <div className="row g-3">
                  {(c.addresses || []).map((addr, idx) => (
                    <div className="col-md-6 col-lg-4" key={idx}>
                      <div className="p-3 border rounded h-100" style={{ backgroundColor: "#f8fafc" }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="badge bg-secondary">{addr.addressType || "Other"}</span>
                          <span className="text-muted small">Address {idx + 1}</span>
                        </div>
                        <address className="mb-0" style={{ fontStyle: "normal", lineHeight: "1.8" }}>
                          {addr.addressLine1 && <div>{addr.addressLine1}</div>}
                          {addr.addressLine2 && <div>{addr.addressLine2}</div>}
                          {(addr.city || addr.state || addr.postalCode) && (
                            <div>
                              {[addr.city, addr.state, addr.postalCode].filter(Boolean).join(", ")}
                            </div>
                          )}
                          {addr.country && (
                            <div className="fw-semibold" style={{ color: "#3a6090" }}>{addr.country}</div>
                          )}
                        </address>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* System Information tab */}
          {activeTab === "system" && (
            <div className="p-3 border rounded bg-white">
              <div className="mb-3">
                {/* <div className="form-check mb-2">
                  <input type="checkbox" className="form-check-input" checked={!!c.primary} disabled readOnly />
                  <label className="form-check-label fw-bold ms-2">PRIMARY CONTACT</label>
                </div> */}
                {c.createdAt && <div className="text-muted small"><strong>Date Created:</strong> {formatDateTime(c.createdAt)}</div>}
              </div>
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                  <button className={`nav-link ${activeSubTab === "systemNotes" ? "active" : ""}`} onClick={() => setActiveSubTab("systemNotes")}>System Notes</button>
                </li>
              </ul>
              {activeSubTab === "systemNotes" && (
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Date</th>
                      <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Set By</th>
                      <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Field</th>
                      <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>Old Value</th>
                      <th className="text-start" style={{ backgroundColor: "#DFE4EB" }}>New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.length === 0
                      ? <tr><td colSpan="5" className="text-muted text-center">No records to show.</td></tr>
                      : auditLogs.map((log, i) => (
                        <tr key={i}>
                          <td className="text-start">{formatDateTime(log.modifiedDate)}</td>
                          <td className="text-start">{log.modifiedBy}</td>
                          <td className="text-start">{log.fieldName}</td>
                          <td className="text-start">{log.oldValue || "—"}</td>
                          <td className="text-start">{log.newValue || "—"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        <div className="mt-3 d-flex gap-3 align-items-center" style={{ fontSize: 13 }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(-1)}>← Back to Contacts</button>
          <Link to="/allcustomers" style={{ color: "#0052CC", textDecoration: "none" }}>All Customers</Link>
        </div>
      </div>
    </>
  );
}